/**
 * BillingContext.jsx — Team Billing & Entitlements Provider
 * ─────────────────────────────────────────────────────────
 * Created: 2026-03-04
 * Purpose: React context that fetches and caches the active team's
 *          billing entitlements so any component can access plan info,
 *          usage stats, and billing status without redundant API calls.
 *
 * Architecture:
 *   - Wraps the app inside AuthProvider (needs teamId from AuthContext)
 *   - Calls billing-entitlements Edge Function on mount and when teamId changes
 *   - Exposes: entitlements, loading, error, refetch(), hasActivePlan, isOverLimit
 *   - Components use useBilling() hook to access billing state
 *
 * Data shape (from billing-entitlements Edge Function):
 *   {
 *     team_id: uuid,
 *     plan: { id, display_name, monthly_price_cents, included_variants, overage_rate_cents },
 *     usage: { active_variants, paused_variants, included_variants, overage_variants,
 *              estimated_overage_cents, usage_pct },
 *     billing: { status, provider, shop_domain, current_period_end, trial_ends_at,
 *                cancel_at_period_end, overage_enabled, hard_cap_enabled,
 *                is_admin, is_shopify, is_shopify_connected, billing_valid,
 *                can_manage_shopify_billing }
 *   }
 *
 * When no billing_accounts row exists (new team, no subscription yet),
 * the Edge Function returns plan.id=null, billing.status='none'.
 *
 * Change log:
 *   2026-03-04  Initial creation. Fetches on teamId change, exposes
 *               computed helpers (hasActivePlan, isOverLimit, isNearLimit).
 *   2026-03-05  Fixed infinite render loop — refetch was recreated on every
 *               auth state change causing useEffect to re-fire endlessly.
 *               Now uses refs for isAuthenticated/teamId so refetch identity
 *               is stable, and the useEffect triggers on primitive values only.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { getEntitlements } from '../services/billingService';

const BillingContext = createContext(null);
const BILLING_FETCH_TIMEOUT_MS = 12000;
const BILLING_FETCH_TIMEOUT_MESSAGE = 'Billing information timed out. Please refresh and try again.';

const withTimeout = async (promise, timeoutMs, timeoutMessage) => {
  let timeoutId;

  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(timeoutMessage));
    }, timeoutMs);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Hook to access billing state from any component.
 * Must be used inside BillingProvider.
 */
export const useBilling = () => {
  const context = useContext(BillingContext);
  if (!context) {
    throw new Error('useBilling must be used within BillingProvider');
  }
  return context;
};

export const BillingProvider = ({ children }) => {
  const { teamId, isAuthenticated } = useAuth();

  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchInFlightRef = useRef(false);
  const lastFetchKeyRef = useRef('');
  const lastFetchAtRef = useRef(0);

  // Use refs so refetch's identity never changes — prevents infinite loops
  // when auth state causes rapid re-renders (e.g., during signUp)
  const authRef = useRef(isAuthenticated);
  const teamRef = useRef(teamId);
  authRef.current = isAuthenticated;
  teamRef.current = teamId;

  /**
   * Fetch entitlements from the billing-entitlements Edge Function.
   * Called when teamId changes, and exposed for manual refresh
   * (e.g. after completing checkout or toggling overage settings).
   *
   * Uses refs for auth/team checks so this callback's identity is
   * stable and won't cause re-render loops via useEffect deps.
   */
  const refetch = useCallback(async () => {
    // Don't fetch if there's no authenticated user or no active team.
    // This prevents 401/400 errors during initial load or after sign-out.
    if (!authRef.current || !teamRef.current) {
      setEntitlements(null);
      return;
    }

    const key = `${authRef.current ? 'auth' : 'anon'}:${teamRef.current || 'none'}`;
    const now = Date.now();
    const withinCooldown = key === lastFetchKeyRef.current && (now - lastFetchAtRef.current < 4000);
    if (fetchInFlightRef.current || withinCooldown) {
      return;
    }

    fetchInFlightRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const data = await withTimeout(
        getEntitlements(),
        BILLING_FETCH_TIMEOUT_MS,
        BILLING_FETCH_TIMEOUT_MESSAGE
      );
      setEntitlements(data);
      lastFetchKeyRef.current = key;
      lastFetchAtRef.current = Date.now();
    } catch (err) {
      console.error('BillingContext: failed to fetch entitlements', err);
      setError(err.message || 'Failed to load billing information');
      // Don't clear entitlements on error — keep stale data visible
    } finally {
      fetchInFlightRef.current = false;
      setLoading(false);
    }
  }, []); // stable — no deps, reads from refs

  // Auto-fetch when teamId changes (primitive value, safe as dep).
  // Skip during initial mount if not authenticated yet.
  useEffect(() => {
    if (isAuthenticated && teamId) {
      setEntitlements(null);
      setError(null);
      refetch();
    } else {
      setEntitlements(null);
      setError(null);
    }
  }, [teamId, isAuthenticated, refetch]);

  // ── Computed helpers ─────────────────────────────────────

  /**
   * True when the team has an active (or trialing) subscription.
   * False for: no plan, canceled, past_due, incomplete.
   * Used to gate features and show upgrade prompts.
   */
  const hasActivePlan = useMemo(() => {
    const status = entitlements?.billing?.status;
    if (entitlements?.billing?.billing_valid === true) return true;
    const hasRealBillingWindow = Boolean(
      entitlements?.billing?.trial_ends_at || entitlements?.billing?.current_period_end
    );

    if (status === 'active') return true;
    if (status === 'trialing') return hasRealBillingWindow;
    return false;
  }, [entitlements]);

  /**
   * True when the team's active variants exceed their included quota.
   * Used by EntitlementBanner to show over-limit warnings.
   */
  const isOverLimit = useMemo(() => {
    if (!entitlements?.usage) return false;
    const { active_variants, included_variants } = entitlements.usage;
    return included_variants > 0 && active_variants > included_variants;
  }, [entitlements]);

  /**
   * True when usage is at or above 80% of included variants.
   * Used by EntitlementBanner to show approaching-limit warnings.
   */
  const isNearLimit = useMemo(() => {
    if (!entitlements?.usage) return false;
    const { usage_pct } = entitlements.usage;
    return usage_pct >= 80 && !isOverLimit;
  }, [entitlements, isOverLimit]);

  /**
   * True when billing is actually owned by Shopify (provider='shopify').
   */
  const isShopifyTeam = useMemo(() => {
    return entitlements?.billing?.is_shopify === true;
  }, [entitlements]);

  /**
   * True when the team has a Shopify-connected store, regardless of whether
   * Shopify billing is active yet.
   */
  const isShopifyConnected = useMemo(() => {
    return entitlements?.billing?.is_shopify_connected === true;
  }, [entitlements]);

  /**
   * True when Shopify is the billing provider and the subscription is usable.
   */
  const hasValidShopifyBilling = useMemo(() => {
    return entitlements?.billing?.billing_valid === true;
  }, [entitlements]);

  /**
   * True when the team is Shopify-connected and should recover billing through
   * Shopify instead of being sent to the app's Stripe pricing page.
   */
  const needsShopifyBillingAction = useMemo(() => {
    const billing = entitlements?.billing;
    if (!billing?.is_shopify_connected) return false;

    const provider = billing.provider ?? null;
    const shouldResolveThroughShopify = !provider || provider === 'shopify';
    return shouldResolveThroughShopify && billing.billing_valid !== true;
  }, [entitlements]);

  const value = useMemo(() => ({
    entitlements,
    loading,
    error,
    refetch,
    hasActivePlan,
    isOverLimit,
    isNearLimit,
    isShopifyTeam,
    isShopifyConnected,
    hasValidShopifyBilling,
    needsShopifyBillingAction,
  }), [entitlements, loading, error, refetch, hasActivePlan, isOverLimit, isNearLimit, isShopifyTeam, isShopifyConnected, hasValidShopifyBilling, needsShopifyBillingAction]);

  return (
    <BillingContext.Provider value={value}>
      {children}
    </BillingContext.Provider>
  );
};
